import { FC, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContainer, ClientContainer } from '@/providers';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/hooks/use-toast';
import { trackEvent } from '@/helpers/external-tools-helper';
import { DEMO_MODE_EVENTS } from '@/analytics/demo-mode';
import { BusinessSwitchLoader } from '@/components/ui/business-switch-loader';
import { useSwitchBusiness } from '@/hooks/use-business-switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

/**
 * Check if current business is a demo account
 */
export function isDemoMode(userState: any): boolean {
  return userState?.business?.is_demo_account === true;
}

/**
 * Persistent banner shown when user is viewing demo business
 * Sleek, compact design with one-click switch to primary business
 */
const DemoModeBanner: FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const location = useLocation();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const switchBusiness = useSwitchBusiness();

  // Get primary business for display in confirmation dialog
  const primaryBusiness = userState.businessOptions?.find(
    (business: any) => business.access.isPrimary
  );

  const handleConfirmExit = useCallback(async () => {
    try {
      setIsLoading(true);
      setShowConfirmation(false);

      // Use the same primaryBusiness we calculated for display
      // It's already filtered by access.isPrimary
      if (!primaryBusiness) {
        setIsLoading(false);
        toast({
          variant: 'destructive',
          title: 'Primary business not found',
          description: 'Unable to find your primary business account.',
        });
        return;
      }

      // Track demo mode exit
      trackEvent(DEMO_MODE_EVENTS.DEMO_MODE_EXITED, {
        businessId: userState.business?.id,
        primaryBusinessId: primaryBusiness.id,
        source: 'banner_button',
      });

      // Use the same business switching logic as account settings
      await switchBusiness({
        business: primaryBusiness,
        currentPath: location.pathname,
        currentClientId: clientState?.selectedClient?.id,
      });

      // Reset loading state after switch completes
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: 'Unable to switch business',
        description: 'Please try again or use the account switcher.',
      });
    }
  }, [
    userState.business?.id,
    primaryBusiness,
    switchBusiness,
    location.pathname,
    clientState?.selectedClient?.id,
  ]);

  const handleExitClick = useCallback(() => {
    setShowConfirmation(true);
  }, []);

  // Show loader even when not in demo mode if we're switching
  if (isLoading) {
    return <BusinessSwitchLoader />;
  }

  // Don't show if not in demo mode - check after all hooks are called
  if (!isDemoMode(userState)) {
    return null;
  }

  return (
    <>
      {/* Top border overlay */}
      <div className="fixed inset-0 pointer-events-none z-[9999]">
        {/* Thicker top border with gradient */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-orange-500 via-orange-400 to-orange-500" />

        {/* Centered Demo Mode label at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-auto">
          <div className="bg-orange-500/95 backdrop-blur-sm text-white px-3 py-1 rounded-b-lg shadow-sm flex items-center gap-1">
            <div className="size-1 rounded-full bg-white/80 animate-pulse" />
            <span className="text-[11px] font-medium">Demo Mode</span>
            <Button
              variant="ghost"
              onClick={handleExitClick}
              disabled={isLoading}
              className="h-5 w-5 p-0 ml-1 hover:bg-white/20 text-white rounded transition-colors flex items-center justify-center"
            >
              {isLoading ? (
                <Spinner size="sm" className="h-3 w-3 text-white" />
              ) : (
                <i className="ti ti-x text-sm" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exit Demo Mode?</DialogTitle>
            <DialogDescription>
              {primaryBusiness ? (
                <>
                  You&apos;ll be switched to your primary account{' '}
                  <span className="font-semibold text-foreground">
                    {primaryBusiness.name || primaryBusiness.business_name}
                  </span>
                  . You can explore demo mode again anytime.
                </>
              ) : (
                <>
                  You&apos;ll return to your primary business account. You can
                  explore demo mode again anytime.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowConfirmation(false)}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmExit}
              disabled={isLoading}
              className="w-full sm:w-auto gap-2"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="h-3 w-3" />
                  <span>Switching...</span>
                </>
              ) : (
                <>
                  <i className="ti ti-arrow-back text-sm" />
                  <span>Exit Demo Mode</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DemoModeBanner;
